import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import packageJson from "../../../../package.json";
import styled, { keyframes } from "styled-components";
import loginBackground from "../../../assets/images/login-redesign/background.jpg";
import botagLogo from "../../../assets/images/login-redesign/botag-logo.png";
import bostagerLogo from "../../../assets/images/login-redesign/bostager-logo.png";
import * as Library from "../../../utils/Library";
import { APP_TITLE, PLATFORM_NAME } from "../../../config/constant";
import moment from "moment/moment";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import DialogBoxComponent from "../../common/dialogs/DialogBoxComponent";
import AppContext, { resetImportantStatesOnLogin } from "../../../AppContext";
import { useParams } from "react-router";
import FormField from "../../common/forms/FormField";
import TermsAndPrivacyPanel from "../../common/termsandConditions/TermsAndPrivacyPanel";

// #region SCOPED CSS
const StyledPage = styled.main`
    position: relative;
    box-sizing: border-box;
    min-height: 100vh;
    min-height: 100dvh;
    width: 100%;
    padding: 80px 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(rgba(0, 0, 0, 0.18), rgba(0, 0, 0, 0.18)),
        #10141b url(${loginBackground}) center / cover no-repeat;
    color: #f5f5f5;
    font-family: Roboto, sans-serif;

    .botag-logo {
        position: absolute;
        top: max(24px, env(safe-area-inset-top));
        left: max(24px, env(safe-area-inset-left));
        width: 66px;
        max-width: calc(100% - 48px);
        height: auto;
    }

    .login-card,
    .login-card * {
        box-sizing: border-box;
    }

    .login-card {
        position: relative;
        width: 100%;
        max-width: 400px;
        min-width: 0;
        padding: 32px 32px 18px;
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: 20px;
        background: rgba(20, 22, 23, 0.94);
        box-shadow: 0 16px 48px rgba(0, 0, 0, 0.3);
    }

    @supports ((backdrop-filter: blur(8px)) or (-webkit-backdrop-filter: blur(8px))) {
        .login-card {
            background: rgba(20, 22, 23, 0.78);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
        }
    }

    .bostager-logo {
        display: block;
        width: 165px;
        max-width: 100%;
        height: auto;
        margin: 0 auto 8px;
    }

    .welcome-back,
    .login-subtitle {
        color: #d1d1d1;
        font-size: 12px;
        line-height: 1.5;
    }

    .welcome-back {
        margin: 0 0 8px;
    }

    .login-subtitle {
        margin: 0 0 24px;
        text-align: center;
        text-transform: none;
    }

    .login-title {
        margin: 0 0 24px;
        font-size: 17px;
        font-weight: 400;
        line-height: 1.4;
    }

    .session-expired-message {
        margin: -8px 0 20px;
        color: #f5a623;
        font-size: 13px;
        line-height: 1.5;
    }

    .credentials {
        display: flex;
        flex-direction: column;
        gap: 18px;
        width: 100%;
        min-width: 0;
    }

    .input-field {
        display: flex;
        flex-direction: column;
        gap: 6px;
        width: 100%;
        min-width: 0;
    }

    .input-label {
        margin: 0;
        color: #e2e2e2;
        font-size: 13px;
        font-weight: 400;
        line-height: 1.5;
    }

    .login-input {
        width: 100%;
        min-width: 0;
        min-height: 44px;
        padding: 10px 12px;
        border: 1px solid #737778;
        border-radius: 6px;
        background: rgba(20, 22, 23, 0.3);
        color: #f5f5f5;
        font: inherit;
        font-size: 16px;
        line-height: 1.4;
    }

    .login-input[aria-invalid="true"] {
        border-color: #ffaaaa;
    }

    .password-field {
        position: relative;
    }

    .password-field .login-input {
        padding-right: 48px;
    }

    .login-card button {
        font-family: inherit;
        cursor: pointer;
    }

    .password-toggle {
        position: absolute;
        top: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 100%;
        min-height: 44px;
        padding: 0;
        border: 0;
        border-radius: 6px;
        background: transparent;
        color: #e2e2e2;
    }

    .forgot-password {
        align-self: flex-end;
        min-height: 32px;
        margin: -12px 0 0;
        padding: 4px 0;
        border: 0;
        background: transparent;
        color: #d4e0e5;
        font-size: 13px;
        text-decoration: underline;
    }

    .login-button {
        align-self: center;
        width: 75%;
        max-width: 235px;
        min-height: 44px;
        padding: 10px 16px;
        border: 1px solid #555555;
        border-radius: 6px;
        background: #090909;
        color: #fff;
        font-size: 16px;
        font-weight: 400;
        line-height: 1.4;
    }

    .login-button:hover:not(:disabled) {
        background: #292929;
    }

    .login-button:active:not(:disabled) {
        background: #181818;
    }

    .login-card button:disabled {
        cursor: not-allowed;
    }

    .login-button:disabled {
        background: #252525;
        color: #b8b8b8;
        border-color: #4a4a4a;
    }

    .login-input:focus-visible,
    .login-card button:focus-visible {
        outline: 2px solid #7ee57e;
        outline-offset: 3px;
    }

    .login-terms {
        margin: 0;
        color: #dedede;
        font-size: 12px;
        line-height: 1.6;
        text-align: center;
        overflow-wrap: anywhere;
    }

    .login-terms button {
        margin: 0;
        padding: 3px 0;
        border: 0;
        background: transparent;
        color: inherit;
        font-size: inherit;
        text-decoration: underline;
    }

    .privacy-policy-link {
        white-space: nowrap;
    }

    .login-footer {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 6px 10px;
        margin-top: 20px;
        padding-top: 14px;
        border-top: 1px solid rgba(255, 255, 255, 0.2);
        color: #d1d1d1;
        font-size: 11px;
        line-height: 1.5;
        text-align: center;
    }

    .login-error {
        margin-bottom: 16px;
        padding: 12px;
        border: 1px solid rgba(255, 170, 170, 0.45);
        border-radius: 6px;
        background: rgba(95, 0, 0, 0.45);
        color: #ffbebe;
        font-size: 13px;
        line-height: 1.5;
        overflow-wrap: anywhere;
    }

    .loading-filters {
        opacity: 0.5;
        filter: blur(4px);
    }

    .DialogPregraf {
        width: 296px;
        font-size: 12px;
        align-items: center;
        font-weight: 400;
        text-align: center;
        color: #ffffff;
        margin-bottom: 16px;
        font-style: normal;
        line-height: normal;
    }

    .DialogLayout {
        margin-bottom: 28px;
    }

    .login-input:-webkit-autofill {
        -webkit-text-fill-color: #ffffff;
        box-shadow: inset 0 0 0 100px #202323;
        animation: login-autofill 0.01s;
    }

    /* A notification only: no visual properties are animated. */
    @keyframes login-autofill {
        from {}
        to {}
    }

    @media (max-width: 400px) {
        .botag-logo {
            top: max(16px, env(safe-area-inset-top));
            left: max(16px, env(safe-area-inset-left));
        }

        .login-card {
            padding: 28px 20px 18px;
        }
    }
`;
const loginAmberSweep = keyframes`
    from { transform: translateX(-100%); }
    to { transform: translateX(300%); }
`;

const StyledLoginLoader = styled.div`
    display: flex;
    justify-content: center;
    position: static;
    width: 100%;

    .child-container {
        display: flex;
        align-items: center;
        justify-content: center;
        align-self: center;
        width: 100%;
    }
    .blur-filter { filter: blur(2px); }
    .login-progress-status {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 99997;
        width: 220px;
        max-width: calc(100% - 24px);
        text-align: center;
        pointer-events: none;
    }
    .login-progress-label {
        display: block;
        margin-bottom: 10px;
        color: #ead2ac;
        font-size: 11px;
        font-weight: 500;
        letter-spacing: 0.16em;
        line-height: 1.4;
    }
    .login-progress-track {
        display: block;
        position: relative;
        overflow: hidden;
        height: 6px;
        border: 1px solid rgba(222, 172, 94, 0.24);
        border-radius: 999px;
        background: rgba(23, 25, 26, 0.88);
        box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.5), 0 0 10px rgba(216, 156, 62, 0.12);
    }
    .login-progress-sweep {
        display: block;
        width: 50%;
        height: 100%;
        border-radius: inherit;
        background: linear-gradient(90deg, transparent, #c58c36 30%, #efbf73 65%, transparent);
        box-shadow: 0 0 6px rgba(239, 191, 115, 0.25);
        animation: ${loginAmberSweep} 1.8s ease-in-out infinite;
    }
    @media (prefers-reduced-motion: reduce) {
        .login-progress-sweep {
            animation: none;
            transform: translateX(50%);
        }
    }
`;

// #endregion

// #region RESET DIALOG PRESENTATION
const resetDialogCSS = `
    && {
        width: 400px;
        max-width: calc(100vw - 32px);
        min-width: 0;
        max-height: calc(100dvh - 32px);
        overflow-y: auto;
        border-radius: 20px;
        font-family: Roboto, sans-serif;
        color: #f5f5f5;
    }
    &&, && * { box-sizing: border-box; }
    && .CardBox {
        width: 100%;
        min-width: 0;
        padding: 28px;
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: 20px;
        background: rgba(20, 22, 23, 0.94);
        box-shadow: 0 16px 48px rgba(0, 0, 0, 0.3);
    }
    @supports ((backdrop-filter: blur(8px)) or (-webkit-backdrop-filter: blur(8px))) {
        && .CardBox {
            background: rgba(20, 22, 23, 0.78);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
        }
    }
    && .CardBox-container-body { padding: 0; min-width: 0; }
    && .CardBox-heading {
        margin: 0 !important;
        padding: 0 !important;
        font-size: 12px;
        font-weight: 400;
        line-height: 1.5;
        letter-spacing: normal;
        text-wrap: wrap !important;
    }
    && .CardBox-container-hr2 {
        width: 100%;
        margin: 16px 0 20px !important;
        background: rgba(255, 255, 255, 0.2);
    }
    && .ready-delete {
        height: auto;
        margin: 0 0 16px;
        font-weight: 500;
        line-height: 1.3;
    }
    && .DialogLayout { width: 100%; min-width: 0; margin-bottom: 24px; }
    && .DialogPregraf {
        display: block;
        width: 100%;
        margin: 0 0 20px;
        color: #d1d1d1;
        font-size: 14px;
        line-height: 1.5;
    }
    && .FormField, && .FormComponentContainer { min-width: 0; width: 100%; }
    && .Frame48 {
        min-width: 0;
        min-height: 44px;
        background: rgba(20, 22, 23, 0.3);
        border: 1px solid #737778;
        border-left: 1px solid #737778 !important;
        border-radius: 6px;
    }
    && .Frame48.invalid { border-color: #ffaaaa; border-left-color: #ffaaaa !important; }
    && .inputFieldContainer { min-width: 0; height: auto; padding: 0 12px; }
    && .Frame48Input {
        min-width: 0;
        min-height: 44px;
        height: 44px !important;
        background: transparent;
        color: #f5f5f5 !important;
        font-family: inherit;
        font-size: 16px;
    }
    && .Frame48.locked .Frame48Input { color: #b8b8b8 !important; }
    && .CardBox-buttons {
        width: 100%;
        gap: 10px;
        margin: 0;
    }
    && .CardBox-buttons > button {
        flex: 1;
        width: auto !important;
        min-width: 0;
        height: auto;
        min-height: 44px;
        padding: 10px 12px;
        border: 1px solid #555555;
        border-radius: 6px;
        background: #090909;
        color: #ffffff;
        font-family: inherit;
        font-weight: 400;
        line-height: 1.4;
    }
    && .CardBox-buttons > button:hover:not(:disabled) { background: #292929; }
    && .CardBox-buttons > button:active:not(:disabled) { background: #181818; }
    && .CardBox-buttons > button:first-child { background: #202323; color: #dedede; }
    && .CardBox-buttons > button:first-child:hover:not(:disabled) { background: #303334; }
    && .CardBox-buttons > button:only-child { flex: 0 1 160px; }
    && .CardBox-buttons > button:disabled {
        background: #252525;
        border-color: #4a4a4a;
        color: #b8b8b8;
        cursor: not-allowed;
    }
    && .Frame48:focus-within, && .CardBox-buttons > button:focus-visible {
        outline: 2px solid #d1d1d1;
        outline-offset: 3px;
    }
`;
// #endregion

// #region LOGIN LOADER
function LoginProgressLoader({ isLoading, children }) {
    return (
        <StyledLoginLoader>
            {isLoading && (
                <div className="login-progress-status" role="status" aria-live="polite" aria-atomic="true">
                    <span className="login-progress-label">SIGNING YOU IN</span>
                    <span className="login-progress-track" aria-hidden="true">
                        <span className="login-progress-sweep" />
                    </span>
                </div>
            )}
            <div className={isLoading ? "child-container blur-filter" : "child-container"}>
                {children}
            </div>
        </StyledLoginLoader>
    );
}
// #endregion

// Edge can visually fill saved credentials while exposing empty input.value
// until the user interacts with the page. Only the browser's autofill state
// affects button readiness; the actual values must be readable to submit.
const matchesBrowserAutofill = input => {
    if (!input) return false;
    return [":autofill", ":-webkit-autofill"].some(selector => {
        try {
            return input.matches(selector);
        } catch {
            // Unsupported selectors must not block ordinary manual login.
            return false;
        }
    });
};

export default function LoginPage() {
    document.title = `${APP_TITLE} - Login`;

    // #region STATES
    const { inviteID } = useParams();
    const { setGetUserDataToken } = React.useContext(AppContext);
    const AppContexts = React.useContext(AppContext);

    const [showPassword, setShowPassword] = useState(false);
    const [loginIsInvalid, setLoginIsInvalid] = useState(false);
    const [emailIsInvalid, setEmailIsInvalid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDialogLoading, setIsDialogLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showTAC, setShowTAC] = useState({
        termsAndConditions: false,
        privacyPolicy: false
    });
    const [email, setEmail] = useState("");
    const [resetEmail, setResetEmail] = useState("");

    const [password, setPassword] = useState("");
    const emailInputRef = useRef(null);
    const passwordInputRef = useRef(null);
    const committedCredentialsRef = useRef({ email, password });
    const deferredSubmitRef = useRef(null);
    const [browserAutofill, setBrowserAutofill] = useState({ email: false, password: false });
    const buttonDisable = !(email || browserAutofill.email) || !(password || browserAutofill.password);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [resetPasswordSent, setResetPasswordSent] = useState(false);
    const showSessionExpired = new URLSearchParams(window.location.search).get("tokenExpired") === "true";
    // #endregion

    // #region METHODS
    const reconcileAutofill = useCallback(() => {
        if (!emailInputRef.current || !passwordInputRef.current) return;
        // Read both before updating controlled values; retain the existing trim behavior.
        const nextEmail = emailInputRef.current.value.trim();
        const nextPassword = passwordInputRef.current.value.trim();
        const emailAutofilled = matchesBrowserAutofill(emailInputRef.current);
        const passwordAutofilled = matchesBrowserAutofill(passwordInputRef.current);
        setBrowserAutofill(previous =>
            previous.email === emailAutofilled && previous.password === passwordAutofilled
                ? previous
                : { email: emailAutofilled, password: passwordAutofilled }
        );
        // Ignore unchanged DOM values so a check cannot overwrite a queued
        // React update (for example, an asynchronously supplied invitation email).
        if (nextEmail !== committedCredentialsRef.current.email) setEmail(nextEmail);
        if (nextPassword !== committedCredentialsRef.current.password) setPassword(nextPassword);
    }, []);

    const handleAutofillAnimation = e => {
        if (e.animationName === "login-autofill") reconcileAutofill();
    };

    const handleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleEmail = e => {
        const em = e.target.value.trim();
        setLoginIsInvalid(false);
        setEmail(em);
        // A deliberate edit overrides any previously recognized saved autofill.
        setBrowserAutofill(current => (current.email ? { ...current, email: false } : current));
    };

    const handlePassword = e => {
        setLoginIsInvalid(false);
        const pwd = e.target.value.trim();
        setPassword(pwd);
        setBrowserAutofill(current => (current.password ? { ...current, password: false } : current));
    };

    const validateEmail = email => {
        const re =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };

    const handleValidation = (liveEmail, livePassword) => {
        if (isLoading || buttonDisable) return false;
        let formIsValid = true;
        if (!liveEmail) {
            formIsValid = false;
            setLoginIsInvalid(true);
        } else if (isNaN(liveEmail)) {
            if (!validateEmail(liveEmail)) {
                formIsValid = false;
                setEmailIsInvalid(true);
            } else setEmailIsInvalid(false);
        }
        if (!livePassword) {
            formIsValid = false;
            setLoginIsInvalid(true);
        }
        return formIsValid;
    };

    const handleLoginSuccess = data => {
        setIsLoading(true);
        localStorage.setItem("token", data?.userID);
        localStorage.setItem("isLoggedIn", data?.isLoggedIn);

        resetImportantStatesOnLogin(AppContexts);

        //Set the token in the context will trigger the user data fetch and redirect to homepage
        setGetUserDataToken(data?.userID);
    };

    const handleSubmit = e => {
        e.preventDefault();
        if (isLoading || buttonDisable || deferredSubmitRef.current !== null) return;

        const submitIfReadable = () => {
            // Never authenticate using the autofill selector: it is only a
            // readiness hint. The real credential values must be available.
            const liveEmail = emailInputRef.current?.value.trim() ?? "";
            const livePassword = passwordInputRef.current?.value.trim() ?? "";
            if (!liveEmail || !livePassword) {
                reconcileAutofill();
                return; // No empty request and no misleading password error.
            }
            setEmailIsInvalid(false);
            setLoginIsInvalid(false);
            setErrorMessage("");
            if (!handleValidation(liveEmail, livePassword)) return;
            if (liveEmail !== committedCredentialsRef.current.email) setEmail(liveEmail);
            if (livePassword !== committedCredentialsRef.current.password) setPassword(livePassword);

            const formData = {
                username: liveEmail,
                password: livePassword,
                rememberToken: true, // TODO: create UI for this in a future task
                platform: PLATFORM_NAME,
                type: "BOstageR"
            };
            setIsLoading(true);

            Library.makeApiRequest({
                endpoint: "Accounts/loginBOstages",
                data: formData,
                setLoader: setIsLoading
            }).then(res => {
                if (res.data.status !== 200) {
                    if (res.data?.data?.error_type === "username") setLoginIsInvalid(true);
                    if (res.data?.data?.error_type === "password") setLoginIsInvalid(true);
                    return;
                }

                if (res.data?.data?.error_type) {
                    //console.log("", res.data?.data?.error_type, res.data?.error_message);
                    if (res.data?.data?.error_type === "groups") setErrorMessage(res.data?.message);
                    setLoginIsInvalid(true);
                    return;
                }

                const accessToken = res.data?.data?.body?.accessToken;
                const refreshTokenCookie = res.data?.data?.headers?.["Set-Cookie"];

                // Extract the refresh token from the Set-Cookie header
                let refreshToken = refreshTokenCookie
                    ? refreshTokenCookie.split(";").find(cookie => cookie.trim().startsWith("refreshToken="))
                    : null;
                refreshToken = refreshToken ? refreshToken.split("=")[1] : null;

                //console.log("Refresh Token:", refreshTokenCookie);
                document.cookie = `refreshToken=${refreshToken}; path=/; Secure; SameSite=Strict; Max-Age=86400`;

                if (!accessToken || !refreshToken) {
                    setLoginIsInvalid(true);
                    console.log("Access token is missing in the response.");
                    return;
                }
                localStorage.setItem("accessToken", accessToken);

                setLoginIsInvalid(false);
                const resData = res.data?.data;
                handleLoginSuccess(resData);
            });
        };

        // One attempt after a genuine user action gives Edge a chance to
        // expose protected saved values; this is NOT another polling loop.
        if ((!emailInputRef.current?.value.trim() || !passwordInputRef.current?.value.trim()) &&
            (browserAutofill.email || browserAutofill.password)) {
            deferredSubmitRef.current = window.setTimeout(() => {
                deferredSubmitRef.current = null;
                submitIfReadable();
            }, 0);
        } else {
            submitIfReadable();
        }
    };

    const handleKeyPress = e => {
        if (e.key === "Enter") {
            handleSubmit(e);
        }
    };

    const getInviteData = inviteID => {
        Library.makeCommonPostRequest("Invites", "getInvite", { inviteID: inviteID }, false).then(res => {
            if (res.data.status !== 200) {
                return;
            }
            const data = res.data.data;

            if (data?.status?.status?.toLowerCase() === "pre-enrolled") {
                updateInviteRequest(inviteID, "Enrolling");
            }
            if (data?.contactEmail) {
                setEmail(data?.contactEmail);
                setPassword("");
            }
        });
    };

    const updateInviteRequest = (inviteID, status) => {
        Library.makeCommonPostRequest("Invites", "updateInvite", { inviteID: inviteID, status: status }, false).then(
            res => {
                if (res.data.status !== 200) {
                    return;
                }
                console.log("Invite updated successfully.");
            }
        );
    };

    const resetRequestHandled = useRef(new Set());
    const resetRequestPending = useRef(false);
    const resetRequestSequence = useRef(0);
    const handleResetPassword = () => {
        if (resetRequestPending.current || !validateEmail(resetEmail)) return;
        resetRequestPending.current = true;
        const submission = ++resetRequestSequence.current;
        setIsDialogLoading(true);
        if (validateEmail(resetEmail))
            Library.makeCommonPostRequest(
                "Emails",
                "requestNewPassword",
                { email: resetEmail, platform: "BOstageR" },
                false,
                value => { setIsDialogLoading(value); if (!value) resetRequestPending.current = false; }
            )
                .then(res => {
                    resetRequestPending.current = false;
                    setIsDialogLoading(false);
                    if (res?.data?.status !== 200 || submission !== resetRequestSequence.current ||
                        resetRequestHandled.current.has(submission)) return;

                    const admission = res.data?.data?.requestAdmission;
                    resetRequestHandled.current.add(submission);
                    if (res.status === 200 && admission?.version === 1 && admission.scope === "request-quota" &&
                        admission.outcome === "quota_exhausted" && Number.isInteger(admission.retryAfterSeconds) &&
                        admission.retryAfterSeconds >= 1 && admission.retryAfterSeconds <= 600) {
                        Library.showToastMessage({ title: "Password reset requests are limited",
                            description: "For security, please wait 10 minutes before requesting another reset email.", type: "warning" });
                        return;
                    }
                    setResetPasswordSent(true);
                })
                .catch(err => {
                    resetRequestPending.current = false;
                    setIsDialogLoading(false);
                });
    };

    const handleCloseResetPassword = () => {
        if (resetPasswordSent) {
            setShowResetPassword(false);
            setResetPasswordSent(false);
            setResetEmail("");
            return;
        }
        setShowResetPassword(false);
        setResetEmail("");
    };

    // #endregion

    // #region EFFECTS
    useEffect(() => () => window.clearTimeout(deferredSubmitRef.current), []);

    useLayoutEffect(() => {
        committedCredentialsRef.current = { email, password };
    }, [email, password]);

    useEffect(() => {
        let checks = [];
        const clearChecks = () => checks.forEach(id => window.clearTimeout(id));
        const reconcileAfterPageShow = () => {
            clearChecks();
            reconcileAutofill();
            // Reconcile promptly while browser restoration is settling.
            checks = [0, 100, 250, 500, 1000, 2000, 4000].map(delay =>
                window.setTimeout(reconcileAutofill, delay)
            );
        };
        reconcileAfterPageShow();
        // Reuse the existing low-frequency check for browser autofill
        // matching, including Edge's protected/unreadable saved credentials.
        // This does not increase polling frequency or submit a request.
        const lateAutofillCheck = window.setInterval(() => {
            if (document.visibilityState !== "hidden") reconcileAutofill();
        }, 1000);
        window.addEventListener("pageshow", reconcileAfterPageShow);
        return () => {
            clearChecks();
            window.clearInterval(lateAutofillCheck);
            window.removeEventListener("pageshow", reconcileAfterPageShow);
        };
    }, [reconcileAutofill]);

    useEffect(() => {
        if (window.location.pathname === "/login") {
            localStorage.clear();
            resetImportantStatesOnLogin(AppContexts);
        }
    }, []);

    useEffect(() => {
        if (inviteID) {
            getInviteData(inviteID);
        }
    }, [inviteID]);

    // #endregion

    // #region COMPONENTS
    const getEyeIcon = () => {
        if (showPassword)
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                    <path
                        d="M7.66196 3.39488C7.9329 3.35482 8.2124 3.33333 8.50028 3.33333C11.9036 3.33333 14.1369 6.33656 14.8871 7.52455C14.9779 7.66833 15.0233 7.74023 15.0488 7.85112C15.0678 7.93439 15.0678 8.06578 15.0487 8.14905C15.0233 8.25993 14.9776 8.3323 14.8861 8.47705C14.6862 8.79343 14.3814 9.23807 13.9777 9.7203M4.98288 4.47669C3.5415 5.45447 2.56297 6.81292 2.11407 7.52352C2.02286 7.66791 1.97725 7.74011 1.95183 7.85099C1.93273 7.93426 1.93272 8.06563 1.95181 8.14891C1.97722 8.25979 2.02262 8.33168 2.11342 8.47545C2.86369 9.66344 5.09694 12.6667 8.50028 12.6667C9.87255 12.6667 11.0546 12.1784 12.0259 11.5177M2.50028 2L14.5003 14M7.08606 6.58579C6.72413 6.94772 6.50028 7.44772 6.50028 8C6.50028 9.10457 7.39571 10 8.50028 10C9.05256 10 9.55256 9.77614 9.91449 9.41421"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        else
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                    <path
                        d="M2.11342 8.47562C2.02262 8.33186 1.97723 8.25998 1.95182 8.14911C1.93273 8.06583 1.93273 7.9345 1.95182 7.85122C1.97723 7.74035 2.02262 7.66847 2.11341 7.52471C2.86369 6.33672 5.09693 3.3335 8.50027 3.3335C11.9036 3.3335 14.1369 6.33672 14.8871 7.52471C14.9779 7.66847 15.0233 7.74035 15.0487 7.85122C15.0678 7.9345 15.0678 8.06583 15.0487 8.14911C15.0233 8.25998 14.9779 8.33186 14.8871 8.47562C14.1369 9.6636 11.9036 12.6668 8.50027 12.6668C5.09693 12.6668 2.86369 9.6636 2.11342 8.47562Z"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M8.50027 10.0002C9.60484 10.0002 10.5003 9.10473 10.5003 8.00016C10.5003 6.89559 9.60484 6.00016 8.50027 6.00016C7.3957 6.00016 6.50027 6.89559 6.50027 8.00016C6.50027 9.10473 7.3957 10.0002 8.50027 10.0002Z"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
    };

    // #endregion

    // #region RENDER
    return (
        <StyledPage>
            <FullScreenOverlay show={showResetPassword}>
                <DialogBoxComponent
                    loading={isDialogLoading}
                    loadingHeading="Sending..."
                    heading={resetPasswordSent ? "SUCCESS!" : "JUST CHECKING..."}
                    headingColor="#d1d1d1"
                    titleColor="#f5f5f5"
                    headingFontSize="20px"
                    loaderBgColor="#d1d1d1"
                    width="100%"
                    customCSS={resetDialogCSS}
                    title={resetPasswordSent ? "Check your email" : "Resetting password?"}
                    onClickYes={handleResetPassword}
                    onClickNo={handleCloseResetPassword}
                    confirmText="RESET"
                    cancelText="CLOSE"
                    useAlternativeLayout={
                        <><p style={{fontSize:12, margin:"12px 0"}}>For security, reset emails have a five-minute cooldown and repeated requests are limited.</p><DialogLayout
                            setResetEmail={setResetEmail}
                            resetEmail={resetEmail}
                            resetPasswordSent={resetPasswordSent}
                        /></>
                    }
                    showConfirm={!resetPasswordSent}
                    buttonWidth={resetPasswordSent ? "30%" : "100%"}
                    cancelState="primary"
                    confirmState={validateEmail(resetEmail) ? "primary" : "disabled"}
                />
            </FullScreenOverlay>
            {showTAC.termsAndConditions && (
                <TermsAndPrivacyPanel
                    type="terms"
                    onCloseClicked={() =>
                        setShowTAC({
                            termsAndConditions: false,
                            privacyPolicy: false
                        })
                    }
                />
            )}
            {showTAC.privacyPolicy && (
                <TermsAndPrivacyPanel
                    type="privacy"
                    onCloseClicked={() =>
                        setShowTAC({
                            termsAndConditions: false,
                            privacyPolicy: false
                        })
                    }
                />
            )}
            <img src={botagLogo} className="botag-logo" alt="BOtag" />
            <section className="login-card" aria-labelledby="login-title" aria-busy={isLoading}>
                <img src={bostagerLogo} className="bostager-logo" alt="BOStageR" />
                <p className="login-subtitle">The BOtag platform for retailers</p>
                <p className="welcome-back">WELCOME BACK</p>
                <h1 id="login-title" className="login-title">LOGIN WITH YOUR CREDENTIALS</h1>
                {showSessionExpired && (
                    <p className="session-expired-message" role="alert">Your session has expired</p>
                )}
                {(loginIsInvalid || emailIsInvalid || errorMessage) && (
                    <div id="login-error" className="login-error" role="alert">
                        {errorMessage
                            ? errorMessage
                            : emailIsInvalid
                            ? "The email address is invalid."
                            : loginIsInvalid
                            ? "The email address or password is incorrect. Please try again."
                            : ""}
                    </div>
                )}
                <LoginProgressLoader isLoading={isLoading}>
                    <div className="credentials">
                        <div className={`input-field ${isLoading ? "loading-filters" : ""}`}>
                            <label className="input-label" htmlFor="login-email">Email address</label>
                            <input
                                id="login-email"
                                ref={emailInputRef}
                                type="email"
                                name="username"
                                autoComplete="username"
                                className="login-input"
                                value={email}
                                aria-invalid={emailIsInvalid || loginIsInvalid}
                                aria-describedby={emailIsInvalid || loginIsInvalid || errorMessage ? "login-error" : undefined}
                                onKeyUp={handleKeyPress}
                                onChange={handleEmail}
                                onInput={reconcileAutofill}
                                onAnimationStart={handleAutofillAnimation}
                            />
                        </div>
                        <div className={`input-field ${isLoading ? "loading-filters" : ""}`}>
                            <label className="input-label" htmlFor="login-password">Password</label>
                            <div className="password-field">
                                <input
                                    id="login-password"
                                    ref={passwordInputRef}
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    autoComplete="current-password"
                                    className="login-input"
                                    value={password}
                                    aria-invalid={loginIsInvalid}
                                    aria-describedby={loginIsInvalid || errorMessage ? "login-error" : undefined}
                                    onChange={handlePassword}
                                    onInput={reconcileAutofill}
                                    onAnimationStart={handleAutofillAnimation}
                                    onKeyUp={handleKeyPress}
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    aria-pressed={showPassword}
                                    onClick={handleShowPassword}>
                                    {getEyeIcon()}
                                </button>
                            </div>
                        </div>
                        <button type="button" className="forgot-password" onClick={() => setShowResetPassword(true)}>
                            Forgot password?
                        </button>
                        <button
                            type="button"
                            className={`login-button ${isLoading ? "loading-filters" : ""}`}
                            disabled={isLoading || buttonDisable}
                            onClick={handleSubmit}>
                            LOGIN
                        </button>
                        <p className={`login-terms ${isLoading ? "loading-filters" : ""}`}>
                            <span>By continuing you agree to BOtag </span>
                            <button
                                type="button"
                                onClick={() => setShowTAC({ termsAndConditions: true, privacyPolicy: false })}>
                                Terms
                            </button>
                            <span> and </span>
                            <span className="privacy-policy-link">
                                <button
                                    type="button"
                                    onClick={() => setShowTAC({ termsAndConditions: false, privacyPolicy: true })}>
                                    Privacy Policy
                                </button>
                                <span>.</span>
                            </span>
                        </p>
                    </div>
                </LoginProgressLoader>
                <footer className="login-footer">
                    <span>BOtag Norge AS © {moment().format("yyyy")}</span>
                    <span aria-hidden="true">|</span>
                    <span>App Version {packageJson.version}</span>
                </footer>
            </section>
        </StyledPage>
    );
    // #endregion
}

const DialogLayout = props => {
    const { setResetEmail, resetEmail, resetPasswordSent } = props;
    return (
        <div className="DialogLayout">
            <label className="DialogPregraf">
                {resetPasswordSent
                    ? "You’ll find a link to reset your password."
                    : "Insert your email. If the email has an account you’ll get the instructions to reset your password"}
            </label>
            <FormField
                type="email"
                requiredType={resetPasswordSent ? "locked" : "mandatory"}
                assistiveTextColorOverride="#d1d1d1"
                varName="email"
                value={resetEmail}
                onTextChange={e => setResetEmail(e)}
            />
        </div>
    );
};
